import { Box, Grid, IconButton, Typography } from '@mui/material';
import dayjs from 'dayjs';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AsyncAutocomplete, type Option } from '../../../common/AsyncAutocomplete';
import DatePickerField from '../../../common/DatePickerField';
import EditIcon from '../../../common/icons/EditIcon';
import SelectField from '../../../common/SelectField';
import TextField from '../../../common/TextField';
import { PERMISSION_IDS } from '../../../../constants/permissions';
import { usePermissions } from '../../../../context/PermissionContext';
import type { DesignationOption, DropdownOption, UserProfileData } from '../../../../services/types/userProfile.types';
import { formatDate } from '../../../../utils/date';
import { toText } from '../../../../utils/common';

export interface ProfileInfoCardProps {
  data: UserProfileData;
  isEditMode: boolean;
  setIsEditMode?: (isEditMode: boolean) => void;
  onChange: (patch: Partial<UserProfileData>) => void;
  errors: { firstName?: string; lastName?: string };
  genders: DropdownOption[];
  designations: DesignationOption[];
  userRoles: DropdownOption[];
  onBlurName?: (field: 'firstName' | 'lastName') => void;
}

function mapDesignations(list: DesignationOption[]): Option[] {
  return list.map((d) => ({
    value: String(d.id),
    label: d.designation,
    id: d.id,
    designation: d.designation,
    designationCode: d.designationCode,
  }));
}

const ProfileInfoCard: React.FC<ProfileInfoCardProps> = ({
  data,
  isEditMode,
  setIsEditMode,
  onChange,
  errors,
  genders,
  designations,
  userRoles,
  onBlurName,
}) => {
  const [isEdit, setIsEdit] = useState(false);
  const { hasPermission } = usePermissions();

  useEffect(() => {
    if (isEditMode === false) {
      setIsEdit(false);
    }
  }, [isEditMode]);

  const genderOptions = useMemo(
    () => genders.map((g) => ({ value: g.id, label: g.name })),
    [genders],
  );
  const roleOptions = useMemo(
    () => userRoles.map((r) => ({ value: r.id, label: r.name })),
    [userRoles],
  );

  const designationOptions = useMemo(() => mapDesignations(designations), [designations]);

  const designationValue: Option | null = useMemo(() => {
    if (data.designationId == null) return null;
    const row = designations.find((d) => d.id === data.designationId);
    if (!row) {
      return {
        value: String(data.designationId),
        label: data.designationName ?? String(data.designationId),
        id: data.designationId,
        designation: data.designationName ?? '',
        designationCode: '',
      };
    }
    return {
      value: String(row.id),
      label: row.designation,
      id: row.id,
      designation: row.designation,
      designationCode: row.designationCode,
    };
  }, [data.designationId, data.designationName, designations]);

  const loadDesignationOptions = useCallback(
    async (searchQuery: string) => {
      const q = searchQuery.trim().toLowerCase();
      const filtered = !q
        ? designationOptions
        : designationOptions.filter(
            (o) =>
              o.label.toLowerCase().includes(q) ||
              String(o.value).includes(q) ||
              String((o as { designationCode?: string }).designationCode ?? '').toLowerCase().includes(q),
          );
      return filtered;
    },
    [designationOptions],
  );

  const genderDisplay =
    data.gender == null ? 'N/A' : genders.find((g) => g.id === data.gender)?.name ?? String(data.gender);
  const roleDisplay =
    data.roleId == null ? 'N/A' : userRoles.find((r) => r.id === data.roleId)?.name ?? String(data.roleId);

  return (
    <Box className="view-section" sx={{ bgcolor: '#F1F3FF', borderRadius: '8px', p: '20px', mb: 2.5 }}>
      <Box sx={{ position: 'relative' }}>
        <Box sx={{ position: 'absolute', top: '-12px', right: '-12px' }}>
          {!isEdit && hasPermission(PERMISSION_IDS.USER_PROFILE_EDIT) && (
            <IconButton
              className="view-edit-icon"
              aria-label="Edit profile information"
              onClick={() => {
                setIsEdit(true);
                setIsEditMode?.(true);
              }}
            >
              <EditIcon />
            </IconButton>
          )}
        </Box>
        <Typography sx={{ fontSize: '16px', fontWeight: 600, mb: 2, color: '#101828' }}>Profile information</Typography>
      {!isEdit ? (
        <>
          <Box className="view-row-box">
            <Box className="view-column-box">
              <div className="view-label-title">First name</div>
              <div className="view-value-title">{toText(data.firstName) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Last name</div>
              <div className="view-value-title">{toText(data.lastName) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Gender</div>
              <div className="view-value-title">{genderDisplay}</div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Employment no.</div>
              <div className="view-value-title">{data.employeeNumber ?? 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">JIRA key</div>
              <div className="view-value-title">{toText(data.jiraKey) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Jira account ID</div>
              <div className="view-value-title">{toText(data.jiraAccountId) || 'N/A'}</div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Email</div>
              <div className="view-value-title">{toText(data.email) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Designation</div>
              <div className="view-value-title">{toText(data.designationName) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">User role</div>
              <div className="view-value-title">{roleDisplay}</div>
            </Box>
          </Box>
          <Box className="view-row-box" sx={{ mt: 2.5 }}>
            <Box className="view-column-box">
              <div className="view-label-title">Designation appointment date</div>
              <div className="view-value-title">
                {data.designationAppointDate ? formatDate(data.designationAppointDate) : 'N/A'}
              </div>
            </Box>
            <Box className="view-column-box">
              <div className="view-label-title">Jira API token</div>
              <div className="view-value-title">{toText(data.jiraApiToken) || 'N/A'}</div>
            </Box>
            <Box className="view-column-box" />
          </Box>
        </>
      ) : (
        <Grid container spacing={2.5}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              id="user-profile-first-name"
              name="firstName"
              label="First name"
              required
              value={data.firstName}
              onChange={(e) => onChange({ firstName: e.target.value })}
              onBlur={() => onBlurName?.('firstName')}
              error={Boolean(errors.firstName)}
              helperText={errors.firstName}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              id="user-profile-last-name"
              name="lastName"
              label="Last name"
              required
              value={data.lastName}
              onChange={(e) => onChange({ lastName: e.target.value })}
              onBlur={() => onBlurName?.('lastName')}
              error={Boolean(errors.lastName)}
              helperText={errors.lastName}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField
              label="Gender"
              value={data.gender ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ gender: v === '' ? null : Number(v) });
              }}
              options={genderOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Employment no." value={String(data.employeeNumber)} disabled />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              id="user-profile-jira-key"
              name="jiraKey"
              label="JIRA key"
              value={toText(data.jiraKey)}
              onChange={(e) => onChange({ jiraKey: e.target.value || null })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              id="user-profile-jira-account-id"
              name="jiraAccountId"
              label="Jira account ID"
              value={toText(data.jiraAccountId)}
              onChange={(e) => onChange({ jiraAccountId: e.target.value || null })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField label="Email" value={data.email} disabled />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <AsyncAutocomplete
              label="Designation"
              value={designationValue}
              onChange={(opt) => {
                if (!opt) {
                  onChange({ designationId: null, designationName: null });
                  return;
                }
                const id = Number(opt.value);
                onChange({
                  designationId: id,
                  designationName: typeof opt.label === 'string' ? opt.label : String(opt.label),
                });
              }}
              loadOptions={loadDesignationOptions}
              loadOptionsOnOpen
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <SelectField
              label="User role"
              value={data.roleId ?? ''}
              onChange={(e) => {
                const v = e.target.value;
                onChange({ roleId: v === '' ? null : Number(v) });
              }}
              options={roleOptions}
              placeholder="None"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <DatePickerField
              label="Designation appointment date"
              value={data.designationAppointDate ? dayjs(data.designationAppointDate) : null}
              onChange={(date) => onChange({ designationAppointDate: date })}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              id="user-profile-jira-api-token"
              name="jiraApiToken"
              label="Jira API token"
              value={toText(data.jiraApiToken)}
              onChange={(e) => onChange({ jiraApiToken: e.target.value || null })}
            />
          </Grid>
        </Grid>
      )}
      </Box>
    </Box>
  );
};

export default ProfileInfoCard;
